import * as signalR from '@microsoft/signalr';
import { useEffect, useState } from 'react';
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import { signal } from '@preact/signals-react';
import IEntityModified from '../../modules/EntityModified';
import { useMainConfig } from '../../modules/config/MainConfigContext';
import { FormMemory } from './entityComponents/Form/FormMemory';

export const entityModified = signal<IEntityModified | undefined>(undefined);

export default function MessageBrokerClient() {
    const { logout } = useAuth0();
    const { config } = useMainConfig();
    const [connection, setConnection] = useState<signalR.HubConnection | null>(null);

    useEffect(() => {
        // Wait for config to load
        if (!config) {
            return;
        }

        const options: signalR.IHttpConnectionOptions = {
            accessTokenFactory: () => {
                const token = localStorage.getItem(process.env.REACT_APP_ACCESS_TOKEN as string);
                return token || ''; // Returning an empty string if the token is not found
            }
        };

        const path = 'message-broker-hub';
        
        // Always connect through the API URL - the backend handles Azure SignalR negotiation
        // When Azure SignalR is enabled, the backend will negotiate with Azure SignalR Service
        // and redirect the client to the appropriate endpoint
        const baseURL = process.env.REACT_APP_API_URL || '';

        // Ensure the base URL does not end with a slash
        const formattedBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
        const fullUrl = new URL(`${formattedBaseURL}/${path}`);

        const newConnection = new signalR.HubConnectionBuilder()
            .withUrl(fullUrl.toString(), options)
            .withAutomaticReconnect()
            .build();

        setConnection(newConnection);

        // Register message handlers BEFORE starting connection
        // This ensures the handlers are always registered, even on automatic reconnects
        // This is critical for Azure SignalR backplane scenarios where reconnections are common
        const handleUpdateComplete = (message: { text: string }) => {
            toast.success(message.text, { position: "top-center" });
        };

        const handleUpdateFailed = (message: { text: string }) => {
            toast.error(message.text, { position: "top-center" });
        };

        const handleUserBlocked = () => {
            FormMemory.clearAll();
            logout({ logoutParams: { returnTo: process.env.REACT_APP_URL || '' } });
        };

        newConnection.on('UpdateComplete', handleUpdateComplete);
        newConnection.on('UpdateFailed', handleUpdateFailed);
        newConnection.on('UserBlocked', handleUserBlocked);

        newConnection.start()
            .then(() => {
            })
            .catch(e => console.error('MessageBroker SignalR connection failed: ', e));
        
        newConnection.onclose((error) => {
        });
        
        newConnection.onreconnecting((error) => {
        });
        
        newConnection.onreconnected((connectionId) => {
            // Handler is already registered
        });

        return () => {
            newConnection.stop();
        };
    }, [logout, config]);

    return null;
}
